import React, {useEffect, useState} from "react";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { BugReport } from "@mui/icons-material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {NavItem} from "../../types";
import {
    AppBar, Drawer, FormControl, Link, Menu, MenuItem, OutlinedInput, Select, SelectChangeEvent, Tooltip,
    useMediaQuery, useTheme
} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {AdminPanelSettings, ArrowBack, ChevronLeft, ChevronRight, Logout} from "@mui/icons-material";
import {useAuth} from "../../context/useAuth";
import {getUserProfile} from "../../services/UserService";
import Box from "@mui/material/Box";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {setActiveAgent} from "../../store/slices/agents.slice";
import {useAppDispatch} from "../../store/hooks";
import {clearSelectedPageIndex, setSelectedPageIndex} from "../../store/slices/nav.slice";
import {ConfirmSaveDialog} from "../confirmSaveDialog/ConfirmSaveDialog";
import {setCancel, setConfirm, setDiscard} from "../../store/slices/changes.slice";
import Typography from "@mui/material/Typography";
import {getActionButtonCallback} from "../../utils/actionButtonHandler";
import {SecondaryButton} from "../button/ButtonVariants";
import {SupportModal} from "../support/SupportModal";

export interface LayoutProps {
    children: React.ReactNode;
    navItems: NavItem[];
}

const drawerWidth = 240;

const MaiLayout = (props: LayoutProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {agents, selectedAgentIndex, selectedAgent} = useSelector((state: RootState) => state.agents);
    const actionButton = useSelector((state: RootState) => state.layout.actionButton);
    const {logout, token} = useAuth();
    const {selectedPageIndex} = useSelector((state: RootState) => state.nav);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const accountMenuOpen = Boolean(anchorEl);
    const {unsavedChanges} = useSelector((state: RootState) => state.changes);
    const [open, setOpen] = useState(false);
    const [openSupport, setOpenSupport] = useState(false);
    const [path, setPath] = useState("");
    const [index, setIndex] = useState(-1);
    const [collapsed, setCollapsed] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const isMessengerActivation = location.pathname.startsWith('/messenger-activation');
    const isAdminPage = location.pathname.startsWith('/admin');
    const showSidebar = location.pathname !== '/agents' && !isMessengerActivation && !isAdminPage // nicht auf Startseite und nicht auf Messenger-Aktivierung

    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    const handleDrawerClose = () => {
        setMobileOpen(false);
    };

    const checkNavigation = (path: string, index: number) => {
        setPath(path);
        setIndex(index)
        if (unsavedChanges) {
            setOpen(true);
            return;
        }
        handleNavigation(path, index);
    }

    const handleNavigation = (p?: string, i?: number) => {
        dispatch(setSelectedPageIndex(i ?? index));
        navigate(p ?? path);
        if (mobileOpen) {
            handleDrawerClose();
        }
    }

    const handleAccountMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleAccountMenuClose();
        logout();
    };

    const handleBack = () => {
        navigate(`/agents`);
        dispatch(clearSelectedPageIndex());
    };

    const handleSelectAgent = (event: SelectChangeEvent) => {
        const agent = agents[+event.target.value];
        dispatch(setActiveAgent(agent));
    };

    const handleAccountMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleConfirm = () => {
        dispatch(setConfirm(true));
        setOpen(false);
        handleNavigation();
    }

    const handleDiscard = () => {
        dispatch(setDiscard(true));
        setOpen(false);
        handleNavigation();
    }

    const handleCancel = () => {
        dispatch(setCancel(true));
        setOpen(false);
    }

    const handleActionClick = () => {
        getActionButtonCallback()?.();
    };

    useEffect(() => {
        if (!selectedAgent) {
            // Erlaube Messenger-Aktivierungs- und Adminseite ohne vorausgewählten Agenten
            if (isMessengerActivation || isAdminPage) return;
            navigate('/agents');
        }
    }, [selectedAgent, location.pathname, isMessengerActivation, isAdminPage, navigate]);

    useEffect(() => {
        const loadProfile = async () => {
            if (!token) return;
            try {
                const res = await getUserProfile(token);
                setIsAdmin(!!res?.data?.isAdmin);
            } catch (e) {
                // ignore, user stays non-admin
            }
        };
        loadProfile();
    }, [token]);

    const accountMenu = (
        <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={accountMenuOpen}
            onClose={handleAccountMenuClose}
            onClick={handleAccountMenuClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
            {isAdmin && (
                <MenuItem
                    onClick={() => navigate('/admin')}
                    sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.custom.hoverGray,
                        },
                    }}
                >
                    <ListItemIcon>
                        <AdminPanelSettings fontSize="small" />
                    </ListItemIcon>
                    Administration
                </MenuItem>
            )}
            <MenuItem
                onClick={() => setOpenSupport(true)}
                sx={{
                    '&:hover': {
                        backgroundColor: theme.palette.custom.hoverGray,
                    },
                }}
            >
                <ListItemIcon>
                    <BugReport fontSize="small" />
                </ListItemIcon>
                Support
            </MenuItem>
            <MenuItem
                onClick={handleLogout}
                sx={{
                    '&:hover': {
                        backgroundColor: theme.palette.custom.hoverGray,
                    },
                }}
            >
                <ListItemIcon>
                    <Logout fontSize="small" />
                </ListItemIcon>
                Abmelden
            </MenuItem>
        </Menu>
    )

    const drawer = (
        <Box pt={'48px'} px={1}>
            <List>
                {props.navItems.map((item, index) => (
                    <ListItem key={item.titel} disablePadding sx={{ justifyContent: collapsed ? 'center' : 'flex-start' }}>
                        <ListItemButton
                            sx={{
                                gap: 1,
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                px: collapsed ? 1 : 2,
                                borderRadius: '10px',
                                '& .MuiListItemIcon-root': {
                                    color: theme.palette.secondary.main,
                                },
                                '& .MuiTypography-root': {
                                    color: theme.palette.secondary.main,
                                },
                                '&:hover': {
                                    backgroundColor: theme.palette.custom.hoverGray,
                                },
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.custom.activeBackground,
                                    '& .MuiListItemIcon-root': {
                                        color: theme.palette.primary.main,
                                    },
                                    '& .MuiTypography-root': {
                                        color: theme.palette.primary.main,
                                    }
                                },
                                '&.Mui-selected:hover': {
                                    backgroundColor: theme.palette.custom.activeBackground,
                                },
                            }}
                            selected={selectedPageIndex === index}
                            onClick={() => checkNavigation(item.path, index)}
                        >
                            <ListItemIcon
                                sx={{ minWidth: 0, justifyContent: 'center' }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            {!collapsed && (
                                <ListItemText>
                                    <Typography sx={{ fontWeight: 700 }}>{item.titel}</Typography>
                                </ListItemText>
                            )}
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <div style={{ display: 'flex', position: 'relative' }}>
            <AppBar position="fixed" elevation={2} sx={{ zIndex: theme.zIndex.drawer + 1 , backgroundColor:'white', height: '48px'}}>
                <Toolbar variant="dense">
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                            {(!isMobile || (isMobile && !showSidebar)) && (
                                <Box sx={{width: drawerWidth}}>
                                    <Link component="button" aria-label={"Zur Übersicht"} onClick={handleBack}>
                                        <img src={"/flottbruuk_logo_navbar_200x70.png"} alt="Logo" style={{height: 36}}/>
                                    </Link>
                                </Box>

                            )}
                            {showSidebar && isMobile && (
                                <IconButton
                                    color="inherit"
                                    edge="start"
                                    onClick={() => setMobileOpen(!mobileOpen)}
                                    sx={{ mr: 1, color: theme.palette.custom.customGray}}
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                                {actionButton && !actionButton.hidden && (
                                    <Box paddingRight={!isMobile ? 6 : 0}>
                                        <SecondaryButton
                                            variant="contained"
                                            disabled={actionButton.disabled}
                                            onClick={handleActionClick}
                                        >
                                            {actionButton.label}
                                        </SecondaryButton>
                                    </Box>

                                )}
                                {showSidebar && (
                                    <>
                                        <Tooltip title="Zur Übersicht">
                                            <IconButton
                                                onClick={handleBack}
                                                size="small"
                                                aria-label="Zur Übersicht"
                                                sx={{ color: theme.palette.secondary.main }}
                                            >
                                                <ArrowBack />
                                            </IconButton>
                                        </Tooltip>
                                        <FormControl sx={{minWidth: 120}} size="small">
                                            <Select
                                                size={"small"}
                                                id="agent-select"
                                                value={selectedAgentIndex?.toString() ?? ''}
                                                label="Agent"
                                                variant={'outlined'}
                                                onChange={handleSelectAgent}
                                                input={
                                                    <OutlinedInput
                                                        notched={false}
                                                        label=""
                                                    />
                                                }
                                                sx={{
                                                    '& .MuiSelect-select': {
                                                        paddingTop: 0.5,
                                                        paddingBottom: 0.5,
                                                    },
                                                }}
                                            >
                                                <MenuItem disabled>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', pl: 0.5 }}>
                                                        Agent
                                                    </Typography>
                                                </MenuItem>
                                                {agents && agents.map((agent, index) => (
                                                    <MenuItem value={index}>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {agent.gcpName}
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </>
                                )}
                                <Tooltip title="Accounteinstellungen">
                                    <IconButton
                                        sx={{ color: theme.palette.secondary.main }}
                                        onClick={handleAccountMenuClick}
                                        aria-controls={accountMenuOpen ? 'account-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={accountMenuOpen ? 'true' : undefined}
                                    >
                                        <AccountCircleIcon sx={{ width: 32, height: 32 }} />
                                    </IconButton>
                                </Tooltip>
                                {accountMenu}
                            </Box>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            {showSidebar && (
                isMobile ? (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={() => setMobileOpen(false)}
                        ModalProps={{ keepMounted: true }}
                    >
                        {drawer}
                    </Drawer>
                ) : (
                    <Box
                        sx={{
                            position: 'relative',
                            width: collapsed ? 60 : drawerWidth,
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.standard,
                            }),
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <Drawer
                            variant="permanent"
                            sx={{
                                width: collapsed ? 60 : drawerWidth,
                                flexShrink: 0,
                                [`& .MuiDrawer-paper`]: {
                                    width: collapsed ? 60 : drawerWidth,
                                    transition: theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.standard,
                                    }),
                                    boxSizing: 'border-box',
                                    overflowX: 'hidden',
                                },
                            }}
                        >
                            {drawer}
                        </Drawer>

                        <Tooltip title={collapsed ? "Aufklappen" : "Zuklappen"}>
                            <IconButton
                                onClick={() => setCollapsed((prev) => !prev)}
                                size="small"
                                sx={{
                                    p: 0,
                                    width: '24px',
                                    height: '24px',
                                    position: 'absolute',
                                    top: 82,
                                    left: collapsed ? 60 : drawerWidth,
                                    transform: 'translateX(-50%)',
                                    backgroundColor: theme.palette.background.paper,
                                    boxShadow: 2,
                                    zIndex: theme.zIndex.drawer + 1,
                                    opacity: collapsed || isHovered ? 1 : 0,
                                    transition: theme.transitions.create(['left', 'opacity'], {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.standard,
                                    }),
                                    pointerEvents: collapsed || isHovered ? 'auto' : 'none',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.custom.customWhite,
                                    },
                                }}
                            >
                                {collapsed ? <ChevronRight sx={{ fontSize: 16, stroke: 'currentColor', strokeWidth: 1.5 }} /> : <ChevronLeft sx={{ fontSize: 16, stroke: 'currentColor', strokeWidth: 1.5 }} />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            )}

            <Box
                component="main"
                sx={{
                    height: { sm: `calc(100vh - 48px)` },
                    width: { sm: `calc(100% - ${collapsed ? 60 : drawerWidth}px)`, xs: '100%' },
                    overflowX: 'hidden',
                    marginTop: '48px',
                    transition: theme.transitions.create(['width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.standard,
                    }),
                }}
            >
                {props.children}
            </Box>

            <ConfirmSaveDialog
                open={open}
                onConfirm={handleConfirm}
                onDiscard={handleDiscard}
                onCancel={handleCancel}
            />

            <SupportModal
                open={openSupport}
                onClose={() => setOpenSupport(false)}
            />
        </div>
    );
};

export default MaiLayout;
